import { ComponentPublicInstance } from 'vue';

import { isHTMLElement, isVueComponent } from '@sg-core/resources/assets/js/utils/dom';

type LoaderType = 'circle' | 'line';

export interface LoadingStateOptions {
  dark?: boolean;
  size?: string;
  text?: string;
  iconOnly?: boolean;
  type?: LoaderType;
}

interface ButtonStateSnapshot {
  loader: HTMLElement;
  role: string | null;
  ariaLive: string | null;
  disabled: boolean;
  textElement?: HTMLElement;
  textHTML?: string | null;
  addedSrOnly?: boolean;
  createdTextElement?: HTMLElement;
}

const BUTTON_LOADING_CLASS = 'phxsg-btn--loading';
const BUTTON_TEXT_CLASS = 'phxsg-btn__text';
const SR_ONLY_CLASS = 'sr-only';
const LOADER_CLASS = 'phxsg-loader';
const LOADER_INNER_CLASS = 'phxsg-loader__inner';
const BUTTON_TEXT_FALLBACK_SELECTORS = ['.sgphx-btn__text', '.btn__text', '[class*="btn__text"]'];

const buttonSnapshots = new WeakMap<HTMLElement, ButtonStateSnapshot>();

type ButtonTarget = HTMLElement | ComponentPublicInstance | null;

function getElement(target: ButtonTarget): HTMLElement | null {
  if (isHTMLElement(target)) {
    return target;
  }

  if (isVueComponent(target)) {
    return target.$el;
  }

  return null;
}

function createLoader(options: LoadingStateOptions) {
  const classes = [
    LOADER_CLASS,
    `${LOADER_CLASS}--${options.type}`,
    `${LOADER_CLASS}--${options.dark ? 'dark' : 'light'}`,
    options.size ? `${LOADER_CLASS}--${options.size}` : null,
  ]
    .filter(Boolean)
    .join(' ');

  const loaderHTML = `
    <div class="${classes}" role="presentation">
      <div class="${LOADER_INNER_CLASS}"></div>
    </div>
  `;

  const container = document.createElement('div');
  container.innerHTML = loaderHTML.trim();

  return container.firstElementChild as HTMLElement;
}

function createTextWrapper(options: LoadingStateOptions) {
  const text = document.createElement('span');
  text.className = options.iconOnly ? SR_ONLY_CLASS : BUTTON_TEXT_CLASS;
  if (options.text) {
    text.textContent = options.text;
  }
  return text;
}

function findButtonTextElement(element: HTMLElement) {
  const selectors = [`.${BUTTON_TEXT_CLASS}`, `.${SR_ONLY_CLASS}`, ...BUTTON_TEXT_FALLBACK_SELECTORS];

  for (const selector of selectors) {
    const node = element.querySelector<HTMLElement>(selector);
    if (node) {
      return node;
    }
  }

  return null;
}

function findNearestChild(element: HTMLElement, descendant: HTMLElement) {
  let current: HTMLElement | null = descendant;

  while (current && current.parentElement && current.parentElement !== element) {
    current = current.parentElement;
  }

  if (current && current.parentElement === element) {
    return current;
  }

  return null;
}

export function addLoadingState(
  target: ButtonTarget,
  options: LoadingStateOptions = {
    size: 'sm',
    text: 'Loading',
    type: 'circle',
  },
) {
  const element = getElement(target);

  if (!element) {
    console.warn('[phxsg::buttons] Unable to find element for addLoadingState');
    return;
  }

  if (buttonSnapshots.has(element)) {
    return;
  }

  const loader = createLoader(options);
  loader.setAttribute('aria-hidden', 'true');

  const snapshot: ButtonStateSnapshot = {
    loader,
    role: element.getAttribute('role'),
    ariaLive: element.getAttribute('aria-live'),
    disabled: element.hasAttribute('disabled'),
  };

  const buttonText = findButtonTextElement(element);

  const referenceNode = buttonText ? findNearestChild(element, buttonText) : null;
  if (referenceNode) {
    element.insertBefore(loader, referenceNode);
  } else if (element.firstChild) {
    element.insertBefore(loader, element.firstChild);
  } else {
    element.appendChild(loader);
  }

  if (options.text) {
    if (buttonText) {
      snapshot.textElement = buttonText;
      snapshot.textHTML = buttonText.innerHTML;

      if (options.iconOnly && !buttonText.classList.contains(SR_ONLY_CLASS)) {
        buttonText.classList.add(SR_ONLY_CLASS);
        snapshot.addedSrOnly = true;
      }

      buttonText.textContent = options.text;
    } else {
      const textWrapper = createTextWrapper(options);
      snapshot.createdTextElement = textWrapper;
      loader.insertAdjacentElement('afterend', textWrapper);
    }
  }

  buttonSnapshots.set(element, snapshot);

  element.classList.add(BUTTON_LOADING_CLASS);
  element.setAttribute('disabled', 'true');
  element.setAttribute('role', 'alert');
  element.setAttribute('aria-live', 'polite');
}

export function removeLoadingState(target: ButtonTarget) {
  const element = getElement(target);

  if (!element) {
    console.warn('[phxsg::buttons] Unable to find element for removeLoadingState');
    return;
  }

  const snapshot = buttonSnapshots.get(element);

  if (!snapshot) {
    return;
  }

  snapshot.loader?.remove();

  if (snapshot.textElement && snapshot.textHTML != null) {
    snapshot.textElement.innerHTML = snapshot.textHTML;
  }

  if (snapshot.addedSrOnly && snapshot.textElement) {
    snapshot.textElement.classList.remove(SR_ONLY_CLASS);
  }

  snapshot.createdTextElement?.remove();

  if (snapshot.role) {
    element.setAttribute('role', snapshot.role);
  } else {
    element.removeAttribute('role');
  }

  if (snapshot.ariaLive) {
    element.setAttribute('aria-live', snapshot.ariaLive);
  } else {
    element.removeAttribute('aria-live');
  }

  if (snapshot.disabled) {
    element.setAttribute('disabled', 'true');
  } else {
    element.removeAttribute('disabled');
  }

  element.classList.remove(BUTTON_LOADING_CLASS);
  buttonSnapshots.delete(element);
}
